import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    RefreshCw, Download, AlertTriangle, CheckCircle2, Loader2,
    Building2, ShieldCheck, Ban, Wallet,
} from "lucide-react";

const STATUS_META = {
    open:       { label: "Ouvert",        color: "#94A3B8", bg: "#F1F5F9" },
    closed:     { label: "Clôturé",        color: "#0B192C", bg: "#E2E8F0" },
    invoiced:   { label: "Facturé",       color: "#0B192C", bg: "#DBEAFE" },
    collecting: { label: "Prélèvement…",  color: "#B45309", bg: "#FEF3C7" },
    collected:  { label: "Encaissé",      color: "#065F46", bg: "#D1FAE5" },
    failed:     { label: "Échec",          color: "#991B1B", bg: "#FEE2E2" },
    cancelled:  { label: "Annulé",        color: "#64748B", bg: "#F1F5F9" },
};

const fmtEUR = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);
const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch { return iso; }
};

/**
 * Admin CRM tab for the deferred-billing J+10 cycles.
 * Lists every BillingPeriod with client info, allows manual retry on failed
 * debits, and lets admin bump the credit_limit of a client.
 */
export default function AdminBilling() {
    const [statusFilter, setStatusFilter] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({ open: 0, collected: 0, failed: 0, total_ttc: 0 });
    const [creditModal, setCreditModal] = useState(null);   // {client_id, current_limit_cents}

    const load = async () => {
        setLoading(true);
        try {
            const url = statusFilter ? `/admin/billing/periods?status=${statusFilter}` : "/admin/billing/periods";
            const { data } = await api.get(url);
            setRows(data.periods || []);
            // Aggregate lightweight stats
            const s = { open: 0, collected: 0, failed: 0, total_ttc: 0 };
            for (const p of data.periods || []) {
                if (p.status === "open") s.open += 1;
                if (p.status === "collected") s.collected += 1;
                if (p.status === "failed") s.failed += 1;
                if (["open", "closed", "invoiced", "collecting"].includes(p.status)) {
                    s.total_ttc += p.total_ttc || 0;
                }
            }
            setStats(s);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur de chargement");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

    const retryPeriod = async (id) => {
        if (!window.confirm("Relancer le prélèvement de ce cycle ?")) return;
        try {
            const { data } = await api.post(`/admin/billing/periods/${id}/retry`);
            toast.success(`Retry lancé — statut : ${data.status}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur retry");
        }
    };

    const downloadPdf = async (id, invoiceNumber) => {
        try {
            const resp = await api.get(`/admin/billing/periods/${id}/invoice.pdf`, {
                responseType: "blob",
            });
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${invoiceNumber || id}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "PDF indisponible");
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-billing-tab">
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Cycles en cours" value={stats.open} icon={Loader2} color="#0B192C" />
                <StatCard label="Encaissés" value={stats.collected} icon={CheckCircle2} color="#065F46" />
                <StatCard label="Échecs à relancer" value={stats.failed} icon={AlertTriangle} color="#991B1B" />
                <StatCard label="Encours total TTC" value={fmtEUR(stats.total_ttc)} icon={Wallet} color="#F5A623" />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
                {["", "open", "closed", "invoiced", "collecting", "collected", "failed", "cancelled"].map((s) => (
                    <button
                        key={s || "all"}
                        onClick={() => setStatusFilter(s)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                            statusFilter === s
                                ? "bg-[#0B192C] text-white"
                                : "bg-white border border-slate-200 text-slate-700 hover:border-slate-400"
                        }`}
                        data-testid={`billing-filter-${s || "all"}`}
                    >
                        {s === "" ? "Tous" : (STATUS_META[s]?.label || s)}
                    </button>
                ))}
                <button
                    onClick={load}
                    className="ml-auto text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                    data-testid="billing-refresh"
                >
                    <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Rafraîchir
                </button>
            </div>

            {/* Table */}
            <div className="sd-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <Th>Cycle</Th>
                            <Th>Client · SIRET</Th>
                            <Th>Missions</Th>
                            <Th align="right">Total TTC</Th>
                            <Th>Statut</Th>
                            <Th>Prélèvement</Th>
                            <Th align="right">Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={7} className="text-center py-8 text-slate-400">
                                    Aucun cycle {statusFilter && `« ${STATUS_META[statusFilter]?.label || statusFilter} »`}
                                </td>
                            </tr>
                        )}
                        {rows.map((p) => {
                            const meta = STATUS_META[p.status] || STATUS_META.open;
                            return (
                                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`billing-row-${p.id}`}>
                                    <Td>
                                        <div className="text-[13px] text-[#0B192C]">
                                            {fmtDate(p.period_start)} → {fmtDate(p.period_end)}
                                        </div>
                                        {p.invoice_number && (
                                            <div className="text-[11px] text-slate-400 font-mono">{p.invoice_number}</div>
                                        )}
                                    </Td>
                                    <Td>
                                        <div className="flex items-center gap-2">
                                            <Building2 size={12} className="text-slate-400" />
                                            <div>
                                                <div className="text-[13px]">
                                                    {p.client_company_name || p.client_email || p.client_id?.slice(0, 8)}
                                                </div>
                                                <div className="text-[11px] text-slate-400 font-mono">
                                                    {p.client_siret || "SIRET —"}
                                                </div>
                                            </div>
                                        </div>
                                    </Td>
                                    <Td>{(p.mission_ids || []).length}</Td>
                                    <Td align="right">
                                        <span className="font-semibold text-[#0B192C]">{fmtEUR(p.total_ttc)}</span>
                                        <div className="text-[11px] text-slate-400">HT {fmtEUR(p.total_ht)}</div>
                                    </Td>
                                    <Td>
                                        <span
                                            className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold"
                                            style={{ background: meta.bg, color: meta.color }}
                                            data-testid={`billing-status-${p.id}`}
                                        >
                                            {meta.label}
                                        </span>
                                        {p.status === "failed" && p.stripe_error && (
                                            <div className="text-[11px] text-red-700 mt-1">{p.stripe_error}</div>
                                        )}
                                    </Td>
                                    <Td>
                                        <div className="text-[13px]">{fmtDate(p.scheduled_debit_at)}</div>
                                        {p.collected_at && (
                                            <div className="text-[11px] text-emerald-700">Encaissé {fmtDate(p.collected_at)}</div>
                                        )}
                                        {p.retry_count > 0 && (
                                            <div className="text-[11px] text-amber-700">Retry {p.retry_count}</div>
                                        )}
                                    </Td>
                                    <Td align="right">
                                        <div className="inline-flex gap-2">
                                            {p.invoice_number && (
                                                <button
                                                    onClick={() => downloadPdf(p.id, p.invoice_number)}
                                                    className="text-xs text-slate-500 hover:text-[#0B192C] inline-flex items-center gap-1"
                                                    data-testid={`billing-pdf-${p.id}`}
                                                >
                                                    <Download size={12} /> PDF
                                                </button>
                                            )}
                                            {p.status === "failed" && (
                                                <button
                                                    onClick={() => retryPeriod(p.id)}
                                                    className="text-xs text-red-700 hover:text-red-900 inline-flex items-center gap-1"
                                                    data-testid={`billing-retry-${p.id}`}
                                                >
                                                    <RefreshCw size={12} /> Retry
                                                </button>
                                            )}
                                            <button
                                                onClick={() => setCreditModal({
                                                    client_id: p.client_id,
                                                    current_limit_cents: p.client_credit_limit_cents || 50000,
                                                    company_name: p.client_company_name,
                                                })}
                                                className="text-xs text-slate-500 hover:text-[#F5A623] inline-flex items-center gap-1"
                                                data-testid={`billing-credit-${p.id}`}
                                            >
                                                <ShieldCheck size={12} /> Crédit
                                            </button>
                                        </div>
                                    </Td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {creditModal && (
                <CreditLimitModal
                    data={creditModal}
                    onClose={() => setCreditModal(null)}
                    onSaved={() => { setCreditModal(null); load(); }}
                />
            )}
        </div>
    );
}

function StatCard({ label, value, icon: Icon, color }) {
    return (
        <div className="sd-card p-4">
            <div className="flex items-center justify-between mb-2">
                <div className="overline">{label}</div>
                <Icon size={14} style={{ color }} />
            </div>
            <div className="font-display text-2xl text-[#0B192C]">{value}</div>
        </div>
    );
}
const Th = ({ children, align }) => (
    <th className={`px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider ${align === "right" ? "text-right" : ""}`}>
        {children}
    </th>
);
const Td = ({ children, align }) => (
    <td className={`px-4 py-3 ${align === "right" ? "text-right" : ""}`}>{children}</td>
);

function CreditLimitModal({ data, onClose, onSaved }) {
    const [newLimit, setNewLimit] = useState(Math.round((data.current_limit_cents || 0) / 100));
    const [freeze, setFreeze] = useState(false);
    const [saving, setSaving] = useState(false);
    const save = async () => {
        setSaving(true);
        try {
            await api.post(`/admin/billing/profiles/${data.client_id}/credit-limit`, {
                credit_limit_cents: Math.round(newLimit * 100),
                credit_status: freeze ? "frozen" : "active",
            });
            toast.success("Plafond mis à jour");
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="credit-limit-modal">
                <div className="overline mb-1">Plafond de crédit</div>
                <h2 className="font-display text-xl mb-4">{data.company_name || "Entreprise"}</h2>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Nouveau plafond (€)</label>
                <input
                    type="number"
                    min={0}
                    step={100}
                    value={newLimit}
                    onChange={(e) => setNewLimit(Number(e.target.value))}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-4"
                    data-testid="credit-limit-input"
                />
                <label className="flex items-center gap-2 text-xs text-slate-700 mb-4">
                    <input type="checkbox" checked={freeze} onChange={(e) => setFreeze(e.target.checked)} className="accent-[#F5A623]" />
                    <Ban size={12} /> Geler le compte (bloque toute nouvelle mission)
                </label>
                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="text-sm text-slate-500">Annuler</button>
                    <button onClick={save} disabled={saving} className="btn-gold text-sm px-4 py-2" data-testid="credit-limit-save">
                        {saving ? <Loader2 size={14} className="animate-spin" /> : "Enregistrer"}
                    </button>
                </div>
            </div>
        </div>
    );
}
